;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:qogita-web-sellers":true});}catch(e){}}();import * as LabelPrimitive from '@radix-ui/react-label'
import { Slot } from '@radix-ui/react-slot'
import {
  ComponentProps,
  ComponentPropsWithoutRef,
  createContext,
  ElementRef,
  forwardRef,
  HTMLAttributes,
  useContext,
  useId,
} from 'react'
import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
  FormProvider,
  useFormContext,
} from 'react-hook-form'

import { cn } from '../../utils/cn'
import { type HelpContent } from './_tooltip/_tooltip-content'
import { HelpIcon } from './help-icon'
import { Label } from './label'

const Form = forwardRef<HTMLFormElement, HTMLAttributes<HTMLFormElement>>(
  (props, ref) => {
    return <form ref={ref} {...props} />
  },
)
Form.displayName = 'Form'

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName
}

const FormFieldContext = createContext<FormFieldContextValue | undefined>(
  undefined,
)

const FormFieldController = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TTransformedValues = TFieldValues,
>({
  ...props
}: ControllerProps<TFieldValues, TName, TTransformedValues>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  )
}

function useFormFieldContext() {
  const context = useContext(FormFieldContext)

  if (!context) {
    throw new Error('useFormFieldContext should be used within <FormField>')
  }

  return context
}

type FormItemContextValue = {
  id: string
}

const FormItemContext = createContext<FormItemContextValue | undefined>(
  undefined,
)

function useFormItemContext() {
  const context = useContext(FormItemContext)

  if (!context) {
    throw new Error('useFormItemContext should be used within <FormItem>')
  }

  return context
}

const FormItem = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & { asChild?: true }
>(({ className, asChild, ...props }, ref) => {
  const id = useId()
  const Component = asChild ? Slot : 'div'
  return (
    <FormItemContext.Provider value={{ id }}>
      <Component
        ref={ref}
        className={cn('flex flex-col gap-1', className)}
        {...props}
      />
    </FormItemContext.Provider>
  )
})
FormItem.displayName = 'FormItem'

export const useFormItem = () => {
  const fieldContext = useFormFieldContext()
  const itemContext = useFormItemContext()
  const { getFieldState, formState } = useFormContext()

  const fieldState = getFieldState(fieldContext.name, formState)

  const { id } = itemContext

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  }
}

export function FormLabel({
  children,
  optional,
  size = 'small',
  className,
  help,
  rumTooltipName,
  ...props
}: ComponentProps<typeof LabelPrimitive.Root> & {
  optional?: boolean
  size?: 'small' | 'large'
  help?: HelpContent
  rumTooltipName?: string
}) {
  const { formItemId } = useFormItem()

  return (
    <div
      className={cn(
        'inline-flex items-center gap-1',
        {
          'typography-body-xs-normal': size === 'small',
          'typography-body-base-normal': size === 'large',
        },
        className,
      )}
    >
      <Label htmlFor={formItemId} size={size} {...props}>
        {children}
      </Label>
      {help ? <HelpIcon help={help} rumTooltipName={rumTooltipName} /> : null}
      {optional ? (
        <span className="text-low-emphasis ml-auto">Optional</span>
      ) : null}
    </div>
  )
}

const FormControl = forwardRef<
  ElementRef<typeof Slot>,
  ComponentPropsWithoutRef<typeof Slot>
>(({ ...props }, ref) => {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormItem()

  return (
    <Slot
      ref={ref}
      id={formItemId}
      aria-describedby={
        error ? `${formDescriptionId} ${formMessageId}` : `${formDescriptionId}`
      }
      aria-invalid={Boolean(error)}
      {...props}
    />
  )
})
FormControl.displayName = 'FormControl'

const FormDescription = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLParagraphElement> & { asChild?: true }
>(({ className, asChild, ...props }, ref) => {
  const { formDescriptionId } = useFormItem()

  const Component = asChild ? Slot : 'p'

  return (
    <Component
      ref={ref}
      id={formDescriptionId}
      className={cn('text-gray-700', className)}
      {...props}
    />
  )
})
FormDescription.displayName = 'FormDescription'

const FormMessage = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLParagraphElement>
>(({ className, children, ...props }, ref) => {
  const { error, formMessageId } = useFormItem()
  const body = children ?? error?.message

  if (!body) {
    return null
  }

  return (
    <p
      ref={ref}
      id={formMessageId}
      className={cn('text-error-700 text-sm', className)}
      {...props}
    >
      {body}
    </p>
  )
})
FormMessage.displayName = 'FormMessage'

export {
  Form,
  FormControl,
  FormDescription,
  FormFieldController,
  FormItem,
  FormMessage,
  FormProvider,
}
